import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from "@angular/core";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { HeaderModel } from "./model/header-model";
import { UserModel } from "./model/user-model";
import { HeaderStyleModel } from "./model/header-style-model";
import { NgClass, NgTemplateOutlet, UpperCasePipe } from "@angular/common";
import { RouterLink } from "@angular/router";

@Component({
  selector: "portal-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  standalone: true,
  imports: [TranslatePipe, NgClass, UpperCasePipe, RouterLink, NgTemplateOutlet],
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  readonly #translateService = inject(TranslateService);
  readonly logoBigUrl = input();
  readonly logoSmallUrl = input();
  readonly isSmallLogoVisible = signal(true);
  @ViewChild("logoSmall") logoSmall!: ElementRef;

  readonly model: InputSignal<HeaderModel> = input<HeaderModel>({
    languageModels: [],
    navigationTabUrls: [],
    loginDropdownUrls: [],
    loginDropdownProductUrls: [],
    logoBigUrl: "",
    logoGastBigUrl: "",
    logoSmallUrl: "",
  });
  readonly style: InputSignal<HeaderStyleModel> = input<HeaderStyleModel>({
    languageDropdownVisible: false,
    loginDropdownVisible: true,
  });
  readonly user: InputSignal<UserModel | null> = input<UserModel | null>({
    username: "",
  });
  readonly currentModel = computed(() => {
    return {
      ...this.model(),
      logoBigUrl: this.logoBigUrl(),
      logoSmallUrl: this.logoSmallUrl(),
    };
  });
  readonly logoAnchorHeight = computed(() => {
    const largeLogoProducts: string[] = ["dtz-", "inl-", "gast-"];
    if (
      !!this.logoSmallUrl() &&
      largeLogoProducts.some((prod: string) => (this.logoSmallUrl() as string).toLowerCase().includes(prod))
    ) {
      return "40px";
    }
    return "25px";
  });
  readonly loginPressed = output<void>();
  readonly logoutPressed = output<void>();
  readonly logoPressed = output<void>();
  readonly languageChanged = output<string>();

  ngAfterViewInit() {
    window.addEventListener("scroll", this.#handleScroll);
  }

  ngOnDestroy() {
    window.removeEventListener("scroll", this.#handleScroll);
  }

  #handleScroll = () => {
    const scrollPosition = window.scrollY || window.pageYOffset;
    this.isSmallLogoVisible.set(scrollPosition <= this.logoSmall.nativeElement.offsetHeight);
  };

  login(): void {
    this.loginPressed.emit();
  }

  logout(): void {
    this.logoutPressed.emit();
  }

  onLanguageChanged(languageCode: string): void {
    this.languageChanged.emit(languageCode);
  }

  isAuthorized(): boolean {
    return !!this.user() && !!this.user()?.username;
  }

  onLogoPressed(): void {
    this.logoPressed.emit();
  }

  getCurrentLanguageCode(): string {
    return this.#translateService.currentLang ? this.#translateService.currentLang.toUpperCase() : "";
  }

  isValidTranslation(key: string): boolean {
    return this.#translateService.instant(key) !== key;
  }
}
